import { z } from "zod";

import DEEPSEEK_MODEL_CATALOG from "./deepseek-model-catalog.json" with { type: "json" };
import {
  SUPPORTED_RUN_MODELS,
  VM0_MODEL_PRICE_TIER,
  type SupportedRunModel,
  type Vm0ModelPriceTier,
} from "./model-price-tiers";
import {
  MODEL_PROVIDER_TYPE_IDS,
  type ModelProviderFramework,
  type ModelProviderType,
} from "./model-provider-types";
export {
  getModelProviderFirewall,
  MODEL_PROVIDER_ENV_PLACEHOLDERS,
  MODEL_PROVIDER_FIREWALL_CONFIGS,
} from "./model-provider-firewalls";
export type {
  ModelProviderFramework,
  ModelProviderType,
} from "./model-provider-types";

export {
  SUPPORTED_RUN_MODELS,
  VM0_MODEL_PRICE_TIER,
  type SupportedRunModel,
  type Vm0ModelPriceTier,
};

/**
 * Secret field configuration for multi-secret providers
 */
export interface SecretFieldConfig {
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  /**
   * When true, this secret is persisted server-side and MUST NOT flow to the
   * runner/sandbox. Used for OAuth refresh tokens and ID tokens that the
   * server holds for refresh + plan-type validation but the sandbox must
   * never see (per #7365). Honored by `resolveMultiAuthProviderSecrets`.
   */
  serverOnly?: boolean;
  /**
   * When true, this secret is populated by a server-side parser from another
   * secret in the same authMethod (typically a single user-input field whose
   * raw value is exploded into multiple stored fields). UI MUST NOT render an
   * input for this secret; the storage validation layer still uses it.
   *
   * Example: `codex-oauth-token` / `auth_json` — user pastes `CODEX_AUTH_JSON`,
   * server parser writes `CHATGPT_ACCESS_TOKEN` / `_REFRESH_TOKEN` /
   * `_ACCOUNT_ID` / `_ID_TOKEN`. Those four are `derived: true`.
   */
  derived?: boolean;
}

/**
 * Auth method configuration for providers with multiple auth options
 */
export interface AuthMethodConfig {
  label: string;
  helpText?: string;
  secrets: Record<string, SecretFieldConfig>;
}

export type ModelProviderEnvBindings = Record<string, string>;

export const modelProviderCodexRuntimeConfigSchema = z.object({
  providerId: z.string().regex(/^[A-Za-z0-9_-]+$/),
  name: z.string().min(1),
  baseUrl: z.url(),
  envKey: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/),
  httpHeaders: z.record(z.string(), z.string()).optional(),
  requiresOpenaiAuth: z.boolean().optional(),
  wireApi: z.literal("responses"),
  supportsWebsockets: z.boolean(),
  modelCatalog: z.record(z.string(), z.unknown()).optional(),
});

export type ModelProviderCodexRuntimeConfig = z.infer<
  typeof modelProviderCodexRuntimeConfigSchema
>;

const MODEL_PROVIDER_CODEX_RUNTIME_CONFIGS: Partial<
  Record<ModelProviderType, ModelProviderCodexRuntimeConfig>
> = {
  deepseek: {
    providerId: "deepseek",
    name: "DeepSeek",
    baseUrl: "https://api.deepseek.com/",
    envKey: "OPENAI_API_KEY",
    requiresOpenaiAuth: false,
    wireApi: "responses",
    supportsWebsockets: false,
    modelCatalog: DEEPSEEK_MODEL_CATALOG,
  },
};

export const DEFAULT_ORG_MODEL_POLICY_MODELS = [
  "claude-fable-5",
  "gpt-5.6-sol",
  "gpt-5.6-luna",
  "deepseek-v4-flash",
] as const satisfies readonly SupportedRunModel[];

export const DEFAULT_ORG_MODEL_POLICY_DEFAULT_MODEL =
  "deepseek-v4-flash" as const satisfies SupportedRunModel;

export const LIMITED_FREE1_DEFAULT_RUN_MODEL =
  "deepseek-v4-flash" as const satisfies SupportedRunModel;

export const supportedRunModelSchema = z.enum(SUPPORTED_RUN_MODELS);

export const modelProviderCredentialScopeSchema = z.enum(["org", "member"]);

export type ModelProviderCredentialScope = z.infer<
  typeof modelProviderCredentialScopeSchema
>;

export interface DefaultOrgModelPolicySeed {
  model: SupportedRunModel;
  isDefault: boolean;
  defaultProviderType: "vm0";
  credentialScope: "org";
  modelProviderId: null;
}

const SUPPORTED_RUN_MODEL_LABELS: Record<SupportedRunModel, string> = {
  "claude-fable-5": "Claude Fable 5",
  "claude-opus-5": "Claude Opus 5",
  "claude-opus-4-8": "Claude Opus 4.8",
  "claude-opus-4-7": "Claude Opus 4.7",
  "claude-opus-4-6": "Claude Opus 4.6",
  "claude-sonnet-5": "Claude Sonnet 5",
  "claude-sonnet-4-6": "Claude Sonnet 4.6",
  "deepseek-v4-flash": "DeepSeek V4 Flash",
  "kimi-k3": "Kimi K3",
  "kimi-k2.7-code": "Kimi K2.7 Code",
  "MiniMax-M3": "MiniMax M3",
  "glm-5.2": "GLM-5.2",
  "glm-5.1": "GLM-5.1",
  "mimo-v2.5": "MiMo-V2.5",
  "hy3-preview": "Hy3 Preview",
  "gpt-5.6-sol": "GPT 5.6 Sol",
  "gpt-5.6-terra": "GPT 5.6 Terra",
  "gpt-5.6-luna": "GPT 5.6 Luna",
  "gpt-5.5": "GPT 5.5",
};

const SUPPORTED_RUN_MODEL_SET: ReadonlySet<string> = new Set(
  SUPPORTED_RUN_MODELS,
);

export function isSupportedRunModel(
  model: string | null | undefined,
): model is SupportedRunModel {
  return typeof model === "string" && SUPPORTED_RUN_MODEL_SET.has(model);
}

/** Models supported by the ChatGPT Codex Fast service tier. */
export const CODEX_FAST_MODE_MODELS = [
  "gpt-5.5",
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
] as const satisfies readonly SupportedRunModel[];

const CODEX_FAST_MODE_MODEL_SET: ReadonlySet<string> = new Set(
  CODEX_FAST_MODE_MODELS,
);

export function isCodexFastModeModel(
  model: string | null | undefined,
): boolean {
  const bareModel = model?.startsWith("openai/")
    ? model.slice("openai/".length)
    : model;
  return (
    typeof bareModel === "string" && CODEX_FAST_MODE_MODEL_SET.has(bareModel)
  );
}

export function getVm0ModelPriceTier(
  model: string,
): Vm0ModelPriceTier | undefined {
  return isSupportedRunModel(model) ? VM0_MODEL_PRICE_TIER[model] : undefined;
}

export function getCanonicalModelDisplayName(model: string): string {
  return isSupportedRunModel(model) ? SUPPORTED_RUN_MODEL_LABELS[model] : model;
}

export function getDefaultOrgModelPolicySeed(
  defaultModel: SupportedRunModel = DEFAULT_ORG_MODEL_POLICY_DEFAULT_MODEL,
): DefaultOrgModelPolicySeed[] {
  return DEFAULT_ORG_MODEL_POLICY_MODELS.map((model) => {
    return {
      model,
      isDefault: model === defaultModel,
      defaultProviderType: "vm0",
      credentialScope: "org",
      modelProviderId: null,
    };
  });
}

/**
 * Mapping from VM0 managed model names to their concrete provider type and vendor.
 * Used at build-context time to resolve the meta-provider to a real provider.
 *
 * NOTE: Defined before MODEL_PROVIDER_TYPES so the vm0 entry can derive its
 * models list from this mapping via Object.keys().
 */
interface Vm0ModelConfig {
  concreteType: ModelProviderType;
  vendor: string;
  // Overrides the display-name when substituting `$model` in the concrete
  // provider's env bindings. Needed when the upstream API expects a
  // different identifier than what we show to users (e.g. OpenRouter uses
  // "z-ai/glm-5.1" while our UI shows "glm-5.1").
  apiModel?: string;
}

// Key order is load-bearing: `Object.keys()` preserves insertion order and
// `MODEL_PROVIDER_TYPES.vm0.models` is derived from it, which in turn drives
// the order models appear in the Built-in model dropdown.
export const VM0_MODEL_TO_PROVIDER: Record<string, Vm0ModelConfig> = {
  "claude-fable-5": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "claude-opus-5": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "claude-opus-4-8": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "claude-opus-4-7": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "claude-opus-4-6": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "claude-sonnet-5": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "claude-sonnet-4-6": {
    concreteType: "anthropic-api-key",
    vendor: "anthropic",
  },
  "glm-5.2": {
    concreteType: "zai-api-key",
    vendor: "zai",
  },
  "glm-5.1": {
    concreteType: "zai-api-key",
    vendor: "zai",
  },
  "mimo-v2.5": {
    concreteType: "openrouter-api-key",
    vendor: "openrouter",
    apiModel: "xiaomi/mimo-v2.5",
  },
  "hy3-preview": {
    concreteType: "openrouter-api-key",
    vendor: "openrouter",
    apiModel: "tencent/hy3-preview",
  },
  "kimi-k3": {
    concreteType: "moonshot-api-key",
    vendor: "moonshot",
  },
  "kimi-k2.7-code": {
    concreteType: "moonshot-api-key",
    vendor: "moonshot",
  },
  "MiniMax-M3": {
    concreteType: "minimax-api-key",
    vendor: "minimax",
  },
  "deepseek-v4-flash": {
    concreteType: "deepseek",
    vendor: "deepseek",
  },
  "gpt-5.6-sol": {
    concreteType: "openai-api-key",
    vendor: "openai",
  },
  "gpt-5.6-terra": {
    concreteType: "openai-api-key",
    vendor: "openai",
  },
  "gpt-5.6-luna": {
    concreteType: "openai-api-key",
    vendor: "openai",
  },
  "gpt-5.5": {
    concreteType: "openai-api-key",
    vendor: "openai",
  },
};

export const VM0_MODEL_ALIAS_TO_MODEL = {
  "anthropic/claude-fable-5": "claude-fable-5",
  "anthropic/claude-opus-5": "claude-opus-5",
  "anthropic/claude-opus-4.8": "claude-opus-4-8",
  "anthropic/claude-opus-4.7": "claude-opus-4-7",
  "anthropic/claude-opus-4.6": "claude-opus-4-6",
  "anthropic/claude-sonnet-5": "claude-sonnet-5",
  "anthropic/claude-sonnet-4.6": "claude-sonnet-4-6",
  "z-ai/glm-5.2": "glm-5.2",
  "z-ai/glm-5.1": "glm-5.1",
  "xiaomi/mimo-v2.5": "mimo-v2.5",
  "tencent/hy3-preview": "hy3-preview",
} as const satisfies Record<string, keyof typeof VM0_MODEL_TO_PROVIDER>;

const VM0_MODEL_ALIAS_LOOKUP: Readonly<Record<string, string>> =
  VM0_MODEL_ALIAS_TO_MODEL;

const LIMITED_FREE1_ALLOWED_RUN_MODELS: ReadonlySet<string> = new Set([
  "gpt-5.6-luna",
  "deepseek-v4-flash",
]);

export function normalizeVm0ModelId(model: string): string {
  return VM0_MODEL_ALIAS_LOOKUP[model] ?? model;
}

export function isLimitedFree1RestrictedRunModel(
  model: string | null | undefined,
): boolean {
  if (!model) {
    return false;
  }
  const normalized = model.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  const canonicalModel = normalizeVm0ModelId(normalized);
  const unprefixedModel = canonicalModel.replace(
    /^(anthropic|deepseek|openai)\//,
    "",
  );
  return !LIMITED_FREE1_ALLOWED_RUN_MODELS.has(unprefixedModel);
}

export type ModelImageInputSupport = "supported" | "unsupported" | "unknown";

const IMAGE_INPUT_SUPPORTED_MODELS = new Set([
  "claude-fable-5",
  "claude-opus-5",
  "claude-opus-4-8",
  "claude-opus-4-7",
  "claude-opus-4-6",
  "claude-sonnet-5",
  "claude-sonnet-4-6",
  "anthropic/claude-fable-5",
  "anthropic/claude-opus-5",
  "anthropic/claude-opus-4.8",
  "anthropic/claude-opus-4.7",
  "anthropic/claude-opus-4.6",
  "anthropic/claude-sonnet-5",
  "anthropic/claude-opus-4.5",
  "anthropic/claude-sonnet-4.6",
  "anthropic/claude-sonnet-4.5",
  "kimi-k3",
  "kimi-k2.7-code",
  "MiniMax-M3",
  "mimo-v2.5",
  "xiaomi/mimo-v2.5",
]);

const IMAGE_INPUT_UNSUPPORTED_MODELS = new Set([
  "glm-5.2",
  "glm-5.1",
  "glm-5",
  "glm-4.7",
  "glm-4.5-air",
  "z-ai/glm-5.2",
  "z-ai/glm-5.1",
  "zai/glm-5-turbo",
  "hy3-preview",
  "tencent/hy3-preview",
  "deepseek-v4-flash",
  "MiniMax-M2.1",
  "minimax/minimax-m2.5",
]);

export function getModelImageInputSupport(
  model: string | null | undefined,
): ModelImageInputSupport {
  if (!model) {
    return "unknown";
  }
  const normalized = normalizeVm0ModelId(model);
  if (
    IMAGE_INPUT_SUPPORTED_MODELS.has(normalized) ||
    IMAGE_INPUT_SUPPORTED_MODELS.has(model)
  ) {
    return "supported";
  }
  if (
    IMAGE_INPUT_UNSUPPORTED_MODELS.has(normalized) ||
    IMAGE_INPUT_UNSUPPORTED_MODELS.has(model)
  ) {
    return "unsupported";
  }
  return "unknown";
}

export function modelSupportsImageInput(
  model: string | null | undefined,
): boolean {
  return getModelImageInputSupport(model) === "supported";
}

/**
 * Return the VM0 managed models visible to callers.
 */
export function getVm0VisibleModels(): string[] {
  return Object.keys(VM0_MODEL_TO_PROVIDER);
}

/**
 * Model Provider type configuration
 * Maps type to framework, secret name, and display info
 *
 * For providers with `envBindings`, the secret is mapped to framework variables:
 * - `$secret` → the stored secret value (legacy single secret)
 * - `$secrets.X` → lookup secret X from the secrets map (multi-secret)
 * - `$model` → the selected model (or default)
 * - Other values are passed through as literals
 *
 * Provider types:
 * - Legacy providers: use `secretName` for single secret
 * - Multi-auth providers: use `authMethods` for multiple auth options with different secrets
 */
export const MODEL_PROVIDER_TYPES = {
  "claude-code-oauth-token": {
    framework: "claude-code" as const,
    secretName: "CLAUDE_CODE_OAUTH_TOKEN",
    label: "Claude Code (OAuth Token)",
    secretLabel: "OAuth token",
    helpText:
      "To get your OAuth token, run: claude setup-token\n(Requires Claude Pro or Max subscription)",
    envBindings: {
      CLAUDE_CODE_OAUTH_TOKEN: "$secret",
      ANTHROPIC_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "claude-fable-5",
      "claude-opus-5",
      "claude-sonnet-5",
      "claude-sonnet-4-6",
      "claude-opus-4-8",
      "claude-opus-4-7",
      "claude-opus-4-6",
    ] as string[],
    defaultModel: "claude-sonnet-4-6",
  },
  "anthropic-api-key": {
    framework: "claude-code" as const,
    secretName: "ANTHROPIC_API_KEY",
    label: "Anthropic",
    secretLabel: "API key",
    helpText:
      "Get your API key at: https://console.anthropic.com/settings/keys",
    envBindings: {
      ANTHROPIC_API_KEY: "$secret",
      ANTHROPIC_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "claude-fable-5",
      "claude-opus-5",
      "claude-sonnet-5",
      "claude-sonnet-4-6",
      "claude-opus-4-8",
      "claude-opus-4-7",
      "claude-opus-4-6",
    ] as string[],
    defaultModel: "claude-sonnet-4-6",
  },
  "openrouter-api-key": {
    framework: "claude-code" as const,
    secretName: "OPENROUTER_API_KEY",
    label: "OpenRouter",
    secretLabel: "API key",
    helpText: "Get your API key at: https://openrouter.ai/settings/keys",
    envBindings: {
      ANTHROPIC_AUTH_TOKEN: "$secret",
      ANTHROPIC_BASE_URL: "https://openrouter.ai/api",
      ANTHROPIC_API_KEY: "",
      ANTHROPIC_MODEL: "$model",
      ANTHROPIC_DEFAULT_OPUS_MODEL: "$model",
      ANTHROPIC_DEFAULT_SONNET_MODEL: "$model",
      ANTHROPIC_DEFAULT_HAIKU_MODEL: "$model",
      CLAUDE_CODE_SUBAGENT_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "anthropic/claude-fable-5",
      "anthropic/claude-opus-5",
      "anthropic/claude-opus-4.8",
      "anthropic/claude-opus-4.7",
      "anthropic/claude-sonnet-5",
      "anthropic/claude-sonnet-4.6",
      "anthropic/claude-opus-4.6",
      "anthropic/claude-opus-4.5",
      "anthropic/claude-sonnet-4.5",
      "z-ai/glm-5.2",
      "z-ai/glm-5.1",
      "xiaomi/mimo-v2.5",
      "tencent/hy3-preview",
    ] as string[],
    defaultModel: "",
  },
  "moonshot-api-key": {
    framework: "claude-code" as const,
    secretName: "MOONSHOT_API_KEY",
    label: "Moonshot (Kimi)",
    secretLabel: "API key",
    helpText:
      "Get your API key at: https://platform.moonshot.ai/console/api-keys",
    envBindings: {
      ANTHROPIC_AUTH_TOKEN: "$secret",
      ANTHROPIC_BASE_URL: "https://api.moonshot.ai/anthropic",
      ANTHROPIC_MODEL: "$model",
      ANTHROPIC_DEFAULT_OPUS_MODEL: "$model",
      ANTHROPIC_DEFAULT_SONNET_MODEL: "$model",
      ANTHROPIC_DEFAULT_HAIKU_MODEL: "$model",
      CLAUDE_CODE_SUBAGENT_MODEL: "$model",
      // Moonshot rejects Anthropic document blocks; keep attachments as text.
      CLAUDE_CODE_DISABLE_ATTACHMENTS: "1",
    } satisfies ModelProviderEnvBindings,
    models: [
      "kimi-k3",
      "kimi-k2.7-code",
      "kimi-k2-thinking-turbo",
      "kimi-k2-thinking",
    ] as string[],
    defaultModel: "kimi-k2.7-code",
  },
  "minimax-api-key": {
    framework: "claude-code" as const,
    secretName: "MINIMAX_API_KEY",
    label: "MiniMax",
    secretLabel: "API key",
    helpText:
      "Get your API key at: https://platform.minimax.io/user-center/basic-information/interface-key",
    envBindings: {
      ANTHROPIC_AUTH_TOKEN: "$secret",
      ANTHROPIC_BASE_URL: "https://api.minimax.io/anthropic",
      ANTHROPIC_MODEL: "$model",
      ANTHROPIC_DEFAULT_OPUS_MODEL: "$model",
      ANTHROPIC_DEFAULT_SONNET_MODEL: "$model",
      ANTHROPIC_DEFAULT_HAIKU_MODEL: "$model",
      CLAUDE_CODE_SUBAGENT_MODEL: "$model",
      API_TIMEOUT_MS: "3000000",
      CLAUDE_CODE_DISABLE_NONESSENTIAL_TRAFFIC: "1",
      // MiniMax does not document support for Anthropic document blocks.
      CLAUDE_CODE_DISABLE_ATTACHMENTS: "1",
    } satisfies ModelProviderEnvBindings,
    models: ["MiniMax-M3", "MiniMax-M2.1"] as string[],
    defaultModel: "MiniMax-M3",
  },
  deepseek: {
    framework: "codex" as const,
    secretName: "DEEPSEEK_API_KEY",
    label: "DeepSeek",
    secretLabel: "API key",
    helpText: "Get your API key at: https://platform.deepseek.com/api_keys",
    envBindings: {
      OPENAI_API_KEY: "$secret",
      OPENAI_BASE_URL: "https://api.deepseek.com/",
      OPENAI_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: ["deepseek-v4-flash"] as string[],
    defaultModel: "deepseek-v4-flash",
  },
  "zai-api-key": {
    framework: "claude-code" as const,
    secretName: "ZAI_API_KEY",
    label: "Z.AI (GLM)",
    secretLabel: "API key",
    helpText: "Get your API key at: https://z.ai/model-api",
    envBindings: {
      ANTHROPIC_AUTH_TOKEN: "$secret",
      ANTHROPIC_BASE_URL: "https://api.z.ai/api/anthropic",
      ANTHROPIC_MODEL: "$model",
      ANTHROPIC_DEFAULT_OPUS_MODEL: "$model",
      ANTHROPIC_DEFAULT_SONNET_MODEL: "$model",
      ANTHROPIC_DEFAULT_HAIKU_MODEL: "$model",
      CLAUDE_CODE_SUBAGENT_MODEL: "$model",
      API_TIMEOUT_MS: "3000000",
    } satisfies ModelProviderEnvBindings,
    models: [
      "glm-5.2",
      "glm-5.1",
      "glm-5",
      "glm-4.7",
      "glm-4.5-air",
    ] as string[],
    defaultModel: "glm-5.2",
  },
  "vercel-ai-gateway": {
    framework: "claude-code" as const,
    secretName: "VERCEL_AI_GATEWAY_API_KEY",
    label: "Vercel AI Gateway",
    secretLabel: "API key",
    helpText: "Get your API key from the Vercel AI Gateway dashboard",
    envBindings: {
      ANTHROPIC_AUTH_TOKEN: "$secret",
      ANTHROPIC_BASE_URL: "https://ai-gateway.vercel.sh",
      ANTHROPIC_API_KEY: "",
      ANTHROPIC_MODEL: "$model",
      ANTHROPIC_DEFAULT_OPUS_MODEL: "$model",
      ANTHROPIC_DEFAULT_SONNET_MODEL: "$model",
      ANTHROPIC_DEFAULT_HAIKU_MODEL: "$model",
      CLAUDE_CODE_SUBAGENT_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "anthropic/claude-fable-5",
      "anthropic/claude-opus-5",
      "anthropic/claude-opus-4.8",
      "anthropic/claude-opus-4.7",
      "anthropic/claude-opus-4.6",
      "anthropic/claude-sonnet-5",
      "anthropic/claude-opus-4.5",
      "anthropic/claude-sonnet-4.6",
      "anthropic/claude-sonnet-4.5",
      "minimax/minimax-m2.5",
      "zai/glm-5-turbo",
    ] as string[],
    defaultModel: "anthropic/claude-sonnet-4.6",
  },
  // Codex-framework twin of openrouter-api-key. Same upstream gateway (OpenRouter)
  // and same API key (shared secretName), but routes through OpenRouter's
  // OpenAI-compatible endpoint surface for GPT models that codex CLI requires.
  // Pairing rule: the claude-code entry serves Anthropic Messages API
  // (/v1/messages); this codex entry serves OpenAI Chat Completions / Responses
  // (/v1/chat/completions, /v1/responses) under the same /api/v1 prefix.
  "openrouter-codex": {
    framework: "codex" as const,
    secretName: "OPENROUTER_API_KEY",
    label: "OpenRouter (Codex)",
    secretLabel: "API key",
    helpText: "Get your API key at: https://openrouter.ai/settings/keys",
    envBindings: {
      OPENAI_API_KEY: "$secret",
      OPENAI_BASE_URL: "https://openrouter.ai/api/v1",
      OPENAI_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "openai/gpt-5.6-sol",
      "openai/gpt-5.6-terra",
      "openai/gpt-5.6-luna",
      "openai/gpt-5.5",
    ] as string[],
    defaultModel: "openai/gpt-5.6-luna",
  },
  // Codex-framework twin of vercel-ai-gateway. Vercel exposes both
  // Anthropic Messages and OpenAI Chat Completions / Responses on the same
  // base URL, distinguished by path. The claude-code entry uses /v1/messages;
  // this codex entry uses /v1/chat/completions or /v1/responses (codex CLI
  // picks the path it needs).
  "vercel-ai-gateway-codex": {
    framework: "codex" as const,
    secretName: "VERCEL_AI_GATEWAY_API_KEY",
    label: "Vercel AI Gateway (Codex)",
    secretLabel: "API key",
    helpText: "Get your API key from the Vercel AI Gateway dashboard",
    envBindings: {
      OPENAI_API_KEY: "$secret",
      OPENAI_BASE_URL: "https://ai-gateway.vercel.sh/v1",
      OPENAI_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "openai/gpt-5.6-sol",
      "openai/gpt-5.6-terra",
      "openai/gpt-5.6-luna",
      "openai/gpt-5.5",
    ] as string[],
    defaultModel: "openai/gpt-5.6-luna",
  },
  "openai-api-key": {
    framework: "codex" as const,
    secretName: "OPENAI_API_KEY",
    label: "OpenAI",
    secretLabel: "API key",
    helpText: "Get your API key at: https://platform.openai.com/api-keys",
    envBindings: {
      OPENAI_API_KEY: "$secret",
      OPENAI_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "gpt-5.6-sol",
      "gpt-5.6-terra",
      "gpt-5.6-luna",
      "gpt-5.5",
    ] as string[],
    defaultModel: "gpt-5.5",
  },
  "codex-oauth-token": {
    framework: "codex" as const,
    label: "ChatGPT (Codex)",
    helpText:
      "Run `codex login` on your machine, then paste the resulting " +
      "~/.codex/auth.json contents to authorize ChatGPT (Plus / Pro / " +
      "Business / Edu / Enterprise) for Codex.",
    authMethods: {
      // Paste-based auth: client posts CODEX_AUTH_JSON, server parses it via
      // codex-auth-json-parser.ts and persists the four derived CHATGPT_*
      // fields. The raw blob is NEVER stored. The wire-shape secret
      // (CODEX_AUTH_JSON) is declared optional+serverOnly so the contract
      // accepts it on POST without persisting; the four CHATGPT_* fields are
      // the canonical stored secrets and the firewall layer reads from those.
      auth_json: {
        label: "Codex auth.json",
        helpText:
          "Run `codex login` locally, then paste the contents of ~/.codex/auth.json below.",
        secrets: {
          CODEX_AUTH_JSON: {
            label: "auth.json contents",
            required: false,
            serverOnly: true,
            placeholder: '{"OPENAI_API_KEY":null,"tokens":{...}}',
            helpText: "Paste the entire contents of ~/.codex/auth.json",
          },
          // CHATGPT_ACCESS_TOKEN and CHATGPT_ACCOUNT_ID reach the sandbox env
          // as placeholder values (substituted by the firewall token-replacement
          // layer at egress) — keeping them non-serverOnly preserves the
          // placeholder injection path. CHATGPT_REFRESH_TOKEN and
          // CHATGPT_ID_TOKEN stay serverOnly per the #7365 invariant.
          //
          // All four are `derived: true` — the server-side parser populates
          // them from the user-pasted CODEX_AUTH_JSON. The UI MUST NOT render
          // them as input fields (per #12024).
          CHATGPT_ACCESS_TOKEN: {
            label: "CHATGPT_ACCESS_TOKEN",
            required: true,
            derived: true,
          },
          CHATGPT_REFRESH_TOKEN: {
            label: "CHATGPT_REFRESH_TOKEN",
            required: true,
            serverOnly: true,
            derived: true,
          },
          CHATGPT_ACCOUNT_ID: {
            label: "CHATGPT_ACCOUNT_ID",
            required: true,
            derived: true,
          },
          CHATGPT_ID_TOKEN: {
            label: "CHATGPT_ID_TOKEN",
            required: true,
            serverOnly: true,
            derived: true,
          },
        },
      },
    } satisfies Record<string, AuthMethodConfig>,
    defaultAuthMethod: "auth_json",
    envBindings: {
      CHATGPT_ACCESS_TOKEN: "$secrets.CHATGPT_ACCESS_TOKEN",
      CHATGPT_ACCOUNT_ID: "$secrets.CHATGPT_ACCOUNT_ID",
      OPENAI_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [
      "gpt-5.6-sol",
      "gpt-5.6-terra",
      "gpt-5.6-luna",
      "gpt-5.5",
    ] as string[],
    defaultModel: "gpt-5.5",
  },
  "azure-foundry": {
    framework: "claude-code" as const,
    label: "Azure Foundry",
    helpText:
      "Run Claude on Microsoft Azure Foundry.\nSetup guide: https://code.claude.com/docs/en/microsoft-foundry",
    authMethods: {
      "api-key": {
        label: "API Key",
        helpText: "Use an Azure Foundry API key for authentication",
        secrets: {
          ANTHROPIC_FOUNDRY_API_KEY: {
            label: "ANTHROPIC_FOUNDRY_API_KEY",
            required: true,
            helpText: "API key from Azure Foundry portal (Endpoints and keys)",
          },
          ANTHROPIC_FOUNDRY_RESOURCE: {
            label: "ANTHROPIC_FOUNDRY_RESOURCE",
            required: true,
            placeholder: "my-resource",
            helpText: "Azure resource name (from portal URL)",
          },
        },
      },
    } satisfies Record<string, AuthMethodConfig>,
    defaultAuthMethod: "api-key",
    envBindings: {
      CLAUDE_CODE_USE_FOUNDRY: "1",
      ANTHROPIC_FOUNDRY_API_KEY: "$secrets.ANTHROPIC_FOUNDRY_API_KEY",
      ANTHROPIC_FOUNDRY_RESOURCE: "$secrets.ANTHROPIC_FOUNDRY_RESOURCE",
      ANTHROPIC_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [] as string[],
    defaultModel: "",
    allowCustomModel: true,
    customModelPlaceholder: "claude-sonnet-4-5",
  },
  "aws-bedrock": {
    framework: "claude-code" as const,
    label: "AWS Bedrock",
    helpText:
      "Run Claude on AWS Bedrock.\nSetup guide: https://code.claude.com/docs/en/amazon-bedrock",
    authMethods: {
      "api-key": {
        label: "Bedrock API Key",
        helpText: "Use a Bedrock API key for authentication",
        secrets: {
          AWS_BEARER_TOKEN_BEDROCK: {
            label: "AWS_BEARER_TOKEN_BEDROCK",
            required: true,
            helpText: "Bedrock API key from AWS console",
          },
          AWS_REGION: {
            label: "AWS_REGION",
            required: true,
            placeholder: "us-east-1",
            helpText: "e.g., us-east-1, us-west-2",
          },
        },
      },
      "access-keys": {
        label: "IAM Access Keys",
        helpText: "Use IAM access key secrets",
        secrets: {
          AWS_ACCESS_KEY_ID: {
            label: "AWS_ACCESS_KEY_ID",
            required: true,
            helpText: "IAM access key ID",
          },
          AWS_SECRET_ACCESS_KEY: {
            label: "AWS_SECRET_ACCESS_KEY",
            required: true,
            helpText: "IAM secret access key",
          },
          AWS_SESSION_TOKEN: {
            label: "AWS_SESSION_TOKEN",
            required: false,
            helpText: "Optional, for temporary secrets",
          },
          AWS_REGION: {
            label: "AWS_REGION",
            required: true,
            placeholder: "us-east-1",
            helpText: "e.g., us-east-1, us-west-2",
          },
        },
      },
    } satisfies Record<string, AuthMethodConfig>,
    defaultAuthMethod: "api-key",
    envBindings: {
      CLAUDE_CODE_USE_BEDROCK: "1",
      AWS_REGION: "$secrets.AWS_REGION",
      AWS_BEARER_TOKEN_BEDROCK: "$secrets.AWS_BEARER_TOKEN_BEDROCK",
      AWS_ACCESS_KEY_ID: "$secrets.AWS_ACCESS_KEY_ID",
      AWS_SECRET_ACCESS_KEY: "$secrets.AWS_SECRET_ACCESS_KEY",
      AWS_SESSION_TOKEN: "$secrets.AWS_SESSION_TOKEN",
      ANTHROPIC_MODEL: "$model",
    } satisfies ModelProviderEnvBindings,
    models: [] as string[],
    defaultModel: "",
    allowCustomModel: true,
    customModelPlaceholder: "anthropic.claude-sonnet-4-20250514-v1:0",
  },
  vm0: {
    framework: "claude-code" as const,
    label: "VM0 Managed",
    models: Object.keys(VM0_MODEL_TO_PROVIDER) as string[],
    defaultModel: DEFAULT_ORG_MODEL_POLICY_DEFAULT_MODEL,
  },
} as const satisfies Record<ModelProviderType, unknown>;

const MODEL_FIRST_PROVIDER_COMPATIBILITY = {
  "claude-fable-5": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "claude-opus-5": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "claude-opus-4-8": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "claude-opus-4-7": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "claude-opus-4-6": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "claude-sonnet-5": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "claude-sonnet-4-6": [
    "vm0",
    "claude-code-oauth-token",
    "anthropic-api-key",
    "openrouter-api-key",
    "vercel-ai-gateway",
  ],
  "gpt-5.6-sol": [
    "vm0",
    "openai-api-key",
    "codex-oauth-token",
    "openrouter-codex",
    "vercel-ai-gateway-codex",
  ],
  "gpt-5.6-terra": [
    "vm0",
    "openai-api-key",
    "codex-oauth-token",
    "openrouter-codex",
    "vercel-ai-gateway-codex",
  ],
  "gpt-5.6-luna": [
    "vm0",
    "openai-api-key",
    "codex-oauth-token",
    "openrouter-codex",
    "vercel-ai-gateway-codex",
  ],
  "gpt-5.5": [
    "vm0",
    "openai-api-key",
    "codex-oauth-token",
    "openrouter-codex",
    "vercel-ai-gateway-codex",
  ],
  "deepseek-v4-flash": ["vm0", "deepseek"],
  "kimi-k3": ["vm0", "moonshot-api-key"],
  "kimi-k2.7-code": ["vm0", "moonshot-api-key"],
  "MiniMax-M3": ["vm0", "minimax-api-key"],
  "glm-5.2": ["vm0", "zai-api-key", "openrouter-api-key"],
  "glm-5.1": ["vm0", "zai-api-key", "openrouter-api-key"],
  "mimo-v2.5": ["vm0", "openrouter-api-key"],
  "hy3-preview": ["vm0", "openrouter-api-key"],
} as const satisfies Record<SupportedRunModel, readonly ModelProviderType[]>;

const PROVIDER_RUNTIME_MODEL_ALIASES: Partial<
  Record<ModelProviderType, Partial<Record<SupportedRunModel, string>>>
> = {
  "openrouter-api-key": {
    "claude-fable-5": "anthropic/claude-fable-5",
    "claude-opus-5": "anthropic/claude-opus-5",
    "claude-opus-4-8": "anthropic/claude-opus-4.8",
    "claude-opus-4-7": "anthropic/claude-opus-4.7",
    "claude-opus-4-6": "anthropic/claude-opus-4.6",
    "claude-sonnet-5": "anthropic/claude-sonnet-5",
    "claude-sonnet-4-6": "anthropic/claude-sonnet-4.6",
    "glm-5.2": "z-ai/glm-5.2",
    "glm-5.1": "z-ai/glm-5.1",
    "mimo-v2.5": "xiaomi/mimo-v2.5",
    "hy3-preview": "tencent/hy3-preview",
  },
  "vercel-ai-gateway": {
    "claude-fable-5": "anthropic/claude-fable-5",
    "claude-opus-5": "anthropic/claude-opus-5",
    "claude-opus-4-8": "anthropic/claude-opus-4.8",
    "claude-opus-4-7": "anthropic/claude-opus-4.7",
    "claude-opus-4-6": "anthropic/claude-opus-4.6",
    "claude-sonnet-5": "anthropic/claude-sonnet-5",
    "claude-sonnet-4-6": "anthropic/claude-sonnet-4.6",
  },
  "openrouter-codex": {
    "gpt-5.6-sol": "openai/gpt-5.6-sol",
    "gpt-5.6-terra": "openai/gpt-5.6-terra",
    "gpt-5.6-luna": "openai/gpt-5.6-luna",
    "gpt-5.5": "openai/gpt-5.5",
  },
  "vercel-ai-gateway-codex": {
    "gpt-5.6-sol": "openai/gpt-5.6-sol",
    "gpt-5.6-terra": "openai/gpt-5.6-terra",
    "gpt-5.6-luna": "openai/gpt-5.6-luna",
    "gpt-5.5": "openai/gpt-5.5",
  },
};

const CANONICAL_RUN_MODEL_ALIASES: Readonly<Record<string, SupportedRunModel>> =
  {
    "anthropic/claude-fable-5": "claude-fable-5",
    "anthropic/claude-opus-5": "claude-opus-5",
    "anthropic/claude-opus-4.8": "claude-opus-4-8",
    "anthropic/claude-opus-4.7": "claude-opus-4-7",
    "anthropic/claude-opus-4.6": "claude-opus-4-6",
    "anthropic/claude-sonnet-5": "claude-sonnet-5",
    "anthropic/claude-sonnet-4.6": "claude-sonnet-4-6",
    "z-ai/glm-5.2": "glm-5.2",
    "z-ai/glm-5.1": "glm-5.1",
    "xiaomi/mimo-v2.5": "mimo-v2.5",
    "tencent/hy3-preview": "hy3-preview",
  };

export function normalizeRunModelId(model: string): string {
  return CANONICAL_RUN_MODEL_ALIASES[model] ?? model;
}

export function getProvidersForModel(model: string): ModelProviderType[] {
  const canonical = normalizeRunModelId(model);
  if (!isSupportedRunModel(canonical)) {
    return [];
  }
  return [...MODEL_FIRST_PROVIDER_COMPATIBILITY[canonical]];
}

export function isModelSupportedByProvider(
  model: string,
  type: ModelProviderType,
): boolean {
  return getProvidersForModel(model).includes(type);
}

export function getProviderRuntimeModel(
  type: ModelProviderType,
  model: string,
): string {
  const canonical = normalizeRunModelId(model);
  if (!isSupportedRunModel(canonical)) {
    return model;
  }
  if (type === "vm0") {
    return VM0_MODEL_TO_PROVIDER[canonical]?.apiModel ?? canonical;
  }
  return PROVIDER_RUNTIME_MODEL_ALIASES[type]?.[canonical] ?? canonical;
}

/**
 * Provider types hidden from user-facing selection UI.
 * These lack static firewall support (dynamic URLs or SigV4), so token
 * replacement cannot be used.  New selection is blocked until a proper
 * solution is implemented; existing configurations continue to work.
 */
const HIDDEN_PROVIDER_LIST = ["aws-bedrock", "azure-foundry"] as const;

const HIDDEN_PROVIDER_TYPES: ReadonlySet<ModelProviderType> = new Set(
  HIDDEN_PROVIDER_LIST,
);

/**
 * Get provider types available for user selection.
 * Excludes providers that are hidden from the UI (e.g., those without token replacement support).
 */
export function getSelectableProviderTypes(): ModelProviderType[] {
  return (Object.keys(MODEL_PROVIDER_TYPES) as ModelProviderType[]).filter(
    (type) => {
      return !HIDDEN_PROVIDER_TYPES.has(type);
    },
  );
}

export const modelProviderTypeSchema = z.enum(MODEL_PROVIDER_TYPE_IDS);

export const modelProviderFrameworkSchema = z.enum(["claude-code", "codex"]);

/**
 * Get the concrete provider type for a VM0 managed model.
 * Throws if the model is not in the VM0 model mapping.
 */
export function getVm0ConcreteProviderType(model: string): ModelProviderType {
  const entry = VM0_MODEL_TO_PROVIDER[model];
  if (!entry) {
    throw new Error(
      `Unknown VM0 model "${model}". Valid models: ${Object.keys(VM0_MODEL_TO_PROVIDER).join(", ")}`,
    );
  }
  return entry.concreteType as ModelProviderType;
}

/**
 * Get the vendor name for a VM0 managed model.
 * Used for key pool lookup.
 */
export function getVm0Vendor(model: string): string {
  const entry = VM0_MODEL_TO_PROVIDER[model];
  if (!entry) {
    throw new Error(
      `Unknown VM0 model "${model}". Valid models: ${Object.keys(VM0_MODEL_TO_PROVIDER).join(", ")}`,
    );
  }
  return entry.vendor;
}

/**
 * Get the upstream API model identifier for a VM0 managed model.
 * Falls back to the display name when no override is configured.
 */
export function getVm0ApiModel(model: string): string {
  const entry = VM0_MODEL_TO_PROVIDER[model];
  if (!entry) {
    throw new Error(
      `Unknown VM0 model "${model}". Valid models: ${Object.keys(VM0_MODEL_TO_PROVIDER).join(", ")}`,
    );
  }
  return entry.apiModel ?? model;
}

/**
 * Get framework for a model provider type
 */
export function getFrameworkForType(
  type: ModelProviderType,
): ModelProviderFramework {
  return MODEL_PROVIDER_TYPES[type]?.framework ?? "claude-code";
}

/**
 * Get secret name for a model provider type (legacy single-secret providers)
 * Returns undefined for multi-auth providers
 */
export function getSecretNameForType(
  type: ModelProviderType,
): string | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  if (!config) return undefined;
  return "secretName" in config ? config.secretName : undefined;
}

/**
 * Check if a model provider type has multiple auth methods
 */
export function hasAuthMethods(type: ModelProviderType): boolean {
  const config = MODEL_PROVIDER_TYPES[type];
  if (!config) return false;
  return "authMethods" in config;
}

/**
 * Get auth methods for a model provider type
 * Returns undefined for legacy single-secret providers
 */
export function getAuthMethodsForType(
  type: ModelProviderType,
): Record<string, AuthMethodConfig> | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  if (!config) return undefined;
  return "authMethods" in config ? config.authMethods : undefined;
}

/**
 * Get default auth method for a model provider type
 * Returns undefined for legacy single-secret providers
 */
export function getDefaultAuthMethod(
  type: ModelProviderType,
): string | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  return "defaultAuthMethod" in config ? config.defaultAuthMethod : undefined;
}

/**
 * Get secrets config for a specific auth method
 * Returns undefined if provider doesn't have auth methods or auth method doesn't exist
 */
export function getSecretsForAuthMethod(
  type: ModelProviderType,
  authMethod: string,
): Record<string, SecretFieldConfig> | undefined {
  const authMethods = getAuthMethodsForType(type);
  if (!authMethods || !(authMethod in authMethods)) {
    return undefined;
  }
  const method = authMethods[authMethod];
  return method?.secrets;
}

/**
 * Get secret names for a specific auth method
 * Returns array of secret names required for the auth method
 */
export function getSecretNamesForAuthMethod(
  type: ModelProviderType,
  authMethod: string,
): string[] | undefined {
  const secrets = getSecretsForAuthMethod(type, authMethod);
  if (!secrets) {
    return undefined;
  }
  return Object.keys(secrets);
}

/**
 * Get runtime environment bindings for a model provider type.
 * Returns undefined for providers without env bindings (use secret directly).
 */
export function getModelProviderEnvBindings(
  type: ModelProviderType,
): ModelProviderEnvBindings | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  return "envBindings" in config ? config.envBindings : undefined;
}

/**
 * Get VM0-owned Codex provider metadata for a static model provider.
 */
export function getModelProviderCodexRuntimeConfig(
  type: ModelProviderType,
): ModelProviderCodexRuntimeConfig | undefined {
  return MODEL_PROVIDER_CODEX_RUNTIME_CONFIGS[type];
}

/**
 * Get the upstream base URL for a model provider type.
 *
 * Returns the framework-appropriate upstream base URL from envBindings —
 * ANTHROPIC_BASE_URL for claude-code, OPENAI_BASE_URL for codex.
 * Returns null when the provider relies on the SDK's default
 * (Anthropic-native providers, OpenAI direct).
 *
 * Used by areProvidersCompatible to detect session-continuation safety
 * across provider swaps. Providers hitting the same upstream URL are
 * compatible; different URLs imply different upstreams and so a
 * potentially different request/response contract.
 */
export function getProviderBaseUrl(type: ModelProviderType): string | null {
  const envBindings = getModelProviderEnvBindings(type);
  if (!envBindings) {
    return null;
  }
  const anthropicUrl = envBindings["ANTHROPIC_BASE_URL"];
  if (anthropicUrl) {
    return anthropicUrl;
  }
  const openaiUrl = envBindings["OPENAI_BASE_URL"];
  return openaiUrl ?? null;
}

/**
 * Check if two model providers are compatible for session continuation.
 * Providers are compatible if they resolve to the same upstream base URL.
 */
export function areProvidersCompatible(
  a: ModelProviderType,
  b: ModelProviderType,
): boolean {
  return getProviderBaseUrl(a) === getProviderBaseUrl(b);
}

/**
 * Get available models for a model provider type
 * Returns undefined for providers without model selection
 */
export function getModels(type: ModelProviderType): string[] | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  return "models" in config ? config.models : undefined;
}

/**
 * Get default model for a model provider type
 * Returns undefined for providers without model selection
 */
export function getDefaultModel(type: ModelProviderType): string | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  return "defaultModel" in config ? config.defaultModel : undefined;
}

/**
 * Check if a model provider type supports model selection
 */
export function hasModelSelection(type: ModelProviderType): boolean {
  const config = MODEL_PROVIDER_TYPES[type];
  // Has predefined models OR allows custom model input
  return (
    ("models" in config && config.models.length > 0) ||
    ("allowCustomModel" in config && config.allowCustomModel === true)
  );
}

/**
 * Check if a model provider allows custom model input
 */
export function allowsCustomModel(type: ModelProviderType): boolean {
  const config = MODEL_PROVIDER_TYPES[type];
  return "allowCustomModel" in config && config.allowCustomModel === true;
}

/**
 * Get custom model placeholder for a model provider type
 */
export function getCustomModelPlaceholder(
  type: ModelProviderType,
): string | undefined {
  const config = MODEL_PROVIDER_TYPES[type];
  return "customModelPlaceholder" in config
    ? config.customModelPlaceholder
    : undefined;
}

export const modelProviderSubscriptionUsageWindowSchema = z.object({
  usedPercent: z.number().nullable(),
  remainingPercent: z.number().nullable(),
  resetAt: z.string().nullable(),
  windowSeconds: z.number().nullable(),
});

export const modelProviderSubscriptionUsageSchema = z.object({
  fiveHour: modelProviderSubscriptionUsageWindowSchema.nullable(),
  weekly: modelProviderSubscriptionUsageWindowSchema.nullable(),
});

/**
 * Model provider response
 */
export const modelProviderResponseSchema = z.object({
  id: z.uuid(),
  type: modelProviderTypeSchema,
  framework: modelProviderFrameworkSchema,
  secretName: z.string().nullable(), // Legacy single-secret (deprecated for multi-auth)
  authMethod: z.string().nullable(), // For multi-auth providers
  secretNames: z.array(z.string()).nullable(), // For multi-auth providers
  isDefault: z.boolean(),
  selectedModel: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // OAuth account metadata populated by provider-specific connect flows. Other
  // provider types omit these.
  accountEmail: z.string().nullable().optional(),
  workspaceName: z.string().nullable().optional(),
  planType: z.string().nullable().optional(),
  // Subscription quota metadata. Providers omit these until an upstream source
  // exposes the reset cadence or next reset timestamp.
  subscriptionResetPeriod: z.string().nullable().optional(),
  subscriptionNextResetAt: z.string().nullable().optional(),
  subscriptionUsage: modelProviderSubscriptionUsageSchema.nullable().optional(),
  subscriptionResetCredits: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .optional(),
  // OAuth refresh state. `needsReconnect` flips to true when the firewall's
  // refresh attempt fails (#11921 writes this on the model_providers row).
  // `lastRefreshErrorCode` carries the typed code from `ChatgptRefreshError`
  // (e.g. `refresh_token_expired`) so the UI can render an actionable
  // re-connect message. Both fields are always emitted for OAuth-typed
  // providers; non-OAuth types default to false / null.
  needsReconnect: z.boolean(),
  lastRefreshErrorCode: z.string().nullable(),
});

export type ModelProviderResponse = z.infer<typeof modelProviderResponseSchema>;

/**
 * List model providers response
 */
export const modelProviderListResponseSchema = z.object({
  modelProviders: z.array(modelProviderResponseSchema),
});

export type ModelProviderListResponse = z.infer<
  typeof modelProviderListResponseSchema
>;

/**
 * Create/update model provider request
 *
 * Legacy providers use `secret` (single string)
 * Multi-auth providers use `authMethod` + `secrets` (map)
 */
export const upsertModelProviderRequestSchema = z.object({
  type: modelProviderTypeSchema,
  secret: z.string().min(1).optional(), // Legacy single secret
  authMethod: z.string().optional(), // For multi-auth providers
  secrets: z.record(z.string(), z.string()).optional(), // For multi-auth providers
  selectedModel: z.string().optional(),
});

export type UpsertModelProviderRequest = z.infer<
  typeof upsertModelProviderRequestSchema
>;

/**
 * Upsert response includes created flag
 */
export const upsertModelProviderResponseSchema = z.object({
  provider: modelProviderResponseSchema,
  created: z.boolean(),
});

export type UpsertModelProviderResponse = z.infer<
  typeof upsertModelProviderResponseSchema
>;

export const orgModelPolicyRouteStatusSchema = z.enum([
  "valid",
  "missing_provider",
  "invalid",
]);

export type OrgModelPolicyRouteStatus = z.infer<
  typeof orgModelPolicyRouteStatusSchema
>;

export const orgModelPolicySchema = z.object({
  id: z.uuid(),
  model: supportedRunModelSchema,
  modelLabel: z.string(),
  isDefault: z.boolean(),
  defaultProviderType: modelProviderTypeSchema,
  credentialScope: modelProviderCredentialScopeSchema,
  modelProviderId: z.uuid().nullable(),
  modelProviderSurfaceId: z.uuid().nullable().optional(),
  routeStatus: orgModelPolicyRouteStatusSchema,
  routeStatusReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type OrgModelPolicy = z.infer<typeof orgModelPolicySchema>;

export const updateOrgModelPolicySchema = z.object({
  model: supportedRunModelSchema,
  isDefault: z.boolean(),
  defaultProviderType: modelProviderTypeSchema,
  credentialScope: modelProviderCredentialScopeSchema,
  modelProviderId: z.uuid().nullable(),
  modelProviderSurfaceId: z.uuid().nullable().optional(),
});

export type UpdateOrgModelPolicy = z.infer<typeof updateOrgModelPolicySchema>;

export const orgModelPoliciesResponseSchema = z.object({
  policies: z.array(orgModelPolicySchema),
  workspaceDefaultModel: supportedRunModelSchema.nullable(),
  workspaceDefaultPolicyId: z.uuid().nullable(),
});

export type OrgModelPoliciesResponse = z.infer<
  typeof orgModelPoliciesResponseSchema
>;

export const updateOrgModelPoliciesRequestSchema = z.object({
  policies: z.array(updateOrgModelPolicySchema),
});

export type UpdateOrgModelPoliciesRequest = z.infer<
  typeof updateOrgModelPoliciesRequestSchema
>;
